import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Webhook, Plus, X, Trash2, Copy, Check, Send, Loader2, AlertCircle, CheckCircle2,
  ScrollText, PauseCircle, Code,
} from 'lucide-react';
import { api } from '@/api/client';

/**
 * One webhook manager, rendered in two places:
 *  - the merchant portal's Developer API page  → `merchantId` omitted, talks to /merchant/webhooks
 *  - the admin merchant profile's Webhooks tab → `merchantId` given, talks to /merchants/:id/webhooks
 *
 * Both hit the SAME backend service, so a webhook our team enters on a merchant's behalf and one the
 * merchant enters themselves are the same row, with the same validation and the same test result.
 * Written once because two copies of a form like this drift the moment either side is changed.
 */
export function WebhookManager({ merchantId }: { merchantId?: number }) {
  const qc = useQueryClient();
  const base = merchantId ? `/merchants/${merchantId}/webhooks` : '/merchant/webhooks';
  const qk = ['merchant-webhooks', merchantId ?? 'me'];

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [testResult, setTestResult] = useState<{ id: number; r: any } | null>(null);
  const [logsFor, setLogsFor] = useState<any>(null);

  const { data: hooks = [], isLoading } = useQuery({
    queryKey: qk,
    queryFn: async () => (await api.get(base)).data,
  });

  const testMut = useMutation({
    mutationFn: async (id: number) => (await api.post(`${base}/${id}/test`, {})).data,
    onSuccess: (r, id) => { setTestResult({ id, r }); qc.invalidateQueries({ queryKey: qk }); },
    onError: (e: any, id) => setTestResult({ id, r: { ok: false, message: e?.response?.data?.message ?? 'Test failed' } }),
  });

  const delMut = useMutation({
    mutationFn: async (id: number) => (await api.delete(`${base}/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk }),
  });

  const toggleMut = useMutation({
    mutationFn: async ({ id, isActive }: { id: number; isActive: boolean }) => (await api.patch(`${base}/${id}`, { isActive })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-semibold text-slate-900 flex items-center gap-2"><Webhook size={16} className="text-emerald-700" /> Tracking Webhooks</div>
          <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">
            We call these URLs whenever a shipment scans, so your own system learns about it without polling us.
            Add the endpoint, pick <b>POST</b> or <b>GET</b>, then press <b>Test</b> — we only treat an HTTP <b>2xx</b> as success.
          </p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }}
          className="px-3 py-2 bg-emerald-700 text-white rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-emerald-800 whitespace-nowrap">
          <Plus size={14} /> Add Webhook
        </button>
      </div>

      <PayloadPreview base={base} />

      {isLoading ? (
        <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : hooks.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-lg p-6 text-center">
          <Webhook size={22} className="mx-auto text-slate-300 mb-2" />
          <div className="text-sm font-medium text-slate-700">No webhook registered</div>
          <p className="text-xs text-slate-500 mt-1">Nothing is being pushed anywhere yet. Add a URL above to start receiving tracking updates.</p>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">URL</th>
                <th className="px-3 py-2">Method</th>
                <th className="px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Last test</th>
                <th className="text-left px-3 py-2">Last delivery</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {hooks.map((h: any) => (
                <tr key={h.id} className="align-top">
                  <td className="px-3 py-2 font-medium text-slate-900">{h.name}</td>
                  <td className="px-3 py-2 max-w-[280px]"><code className="text-[11px] break-all text-slate-600">{h.url}</code></td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-[11px] px-1.5 py-0.5 rounded font-mono ${h.method === 'POST' ? 'bg-indigo-50 text-indigo-700' : 'bg-amber-50 text-amber-700'}`}>{h.method}</span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {h.isActive
                      ? <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">Active</span>
                      : <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Paused</span>}
                    {h.consecutiveFailures > 0 && (
                      <div className="text-[10px] text-rose-600 mt-0.5">{h.consecutiveFailures} failed in a row</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {h.lastTestAt ? (
                      <span className={h.lastTestStatus >= 200 && h.lastTestStatus < 300 ? 'text-emerald-700' : 'text-rose-600'}>
                        {h.lastTestStatus || '—'} · {h.lastTestMs}ms
                        <div className="text-[10px] text-slate-400">{new Date(h.lastTestAt).toLocaleString('en-IN')}</div>
                      </span>
                    ) : <span className="text-slate-400">never tested</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {h.lastDeliveryAt ? (
                      <span className={h.lastDeliveryStatus >= 200 && h.lastDeliveryStatus < 300 ? 'text-emerald-700' : 'text-rose-600'}>
                        {h.lastDeliveryStatus || 'no response'}
                        <div className="text-[10px] text-slate-400">{new Date(h.lastDeliveryAt).toLocaleString('en-IN')}</div>
                      </span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1 justify-end">
                      <button title="Send a test call" disabled={testMut.isPending}
                        onClick={() => { setTestResult(null); testMut.mutate(h.id); }}
                        className="border border-slate-200 px-2 py-1 rounded text-xs flex items-center gap-1 hover:bg-slate-50 disabled:opacity-50">
                        {testMut.isPending && testMut.variables === h.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Test
                      </button>
                      <button title="Delivery log" onClick={() => setLogsFor(h)}
                        className="border border-slate-200 px-2 py-1 rounded text-xs hover:bg-slate-50"><ScrollText size={12} /></button>
                      <button title={h.isActive ? 'Pause' : 'Resume'} onClick={() => toggleMut.mutate({ id: h.id, isActive: !h.isActive })}
                        className="border border-slate-200 px-2 py-1 rounded text-xs hover:bg-slate-50"><PauseCircle size={12} /></button>
                      <button title="Edit" onClick={() => { setEditing(h); setShowForm(true); }}
                        className="border border-slate-200 px-2 py-1 rounded text-xs hover:bg-slate-50"><Code size={12} /></button>
                      <button title="Delete"
                        onClick={() => { if (confirm(`Delete webhook "${h.name}"? We will stop pushing tracking updates to it.`)) delMut.mutate(h.id); }}
                        className="border border-rose-200 text-rose-600 px-2 py-1 rounded text-xs hover:bg-rose-50"><Trash2 size={12} /></button>
                    </div>
                    {h.disabledReason && <div className="text-[10px] text-amber-700 mt-1 max-w-[260px] text-right">{h.disabledReason}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {testResult && <TestResultPanel result={testResult.r} onClose={() => setTestResult(null)} />}
      {showForm && <WebhookForm base={base} existing={editing} onClose={() => { setShowForm(false); setEditing(null); }}
        onSaved={() => { setShowForm(false); setEditing(null); qc.invalidateQueries({ queryKey: qk }); }} />}
      {logsFor && <DeliveryLog base={base} hook={logsFor} onClose={() => setLogsFor(null)} />}
    </div>
  );
}

/**
 * "What we will send" — fetched from the backend rather than hard-coded here, because it is produced
 * by the SAME function that builds a real delivery. A sample typed into the UI by hand is a sample
 * that stops matching reality the first time the payload changes, and a merchant who mapped their ERP
 * against it finds out in production.
 */
function PayloadPreview({ base }: { base: string }) {
  const [method, setMethod] = useState<'POST' | 'GET'>('POST');
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { data } = useQuery({
    queryKey: ['webhook-sample', base, method],
    queryFn: async () => (await api.get(`${base}/sample-payload`, { params: { method } })).data,
  });

  const text = useMemo(() => {
    if (!data) return '';
    return method === 'POST' ? JSON.stringify(data.body, null, 2) : data.example;
  }, [data, method]);

  return (
    <div className="border border-slate-200 rounded-lg bg-slate-50">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-2.5 text-left">
        <span className="text-sm font-medium text-slate-800 flex items-center gap-2">
          <Code size={14} className="text-slate-500" /> Payload preview — exactly what we will send
        </span>
        <span className="text-xs text-slate-500">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2">
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-0.5 w-fit">
            {(['POST', 'GET'] as const).map(m => (
              <button key={m} onClick={() => setMethod(m)}
                className={`px-3 py-1 text-xs rounded-md ${method === m ? 'bg-emerald-700 text-white' : 'text-slate-600'}`}>{m}</button>
            ))}
          </div>
          {data?.note && <p className="text-xs text-slate-600">{data.note}</p>}
          {method === 'POST' && data?.headers && (
            <pre className="bg-slate-900 text-sky-300 text-[11px] p-3 rounded overflow-x-auto"><code>{Object.entries(data.headers).map(([k, v]) => `${k}: ${v}`).join('\n')}</code></pre>
          )}
          <div className="relative">
            <pre className="bg-slate-900 text-emerald-300 text-[11px] p-3 rounded overflow-x-auto max-h-80"><code>{text}</code></pre>
            <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
              className="absolute top-2 right-2 bg-white/10 text-white px-2 py-1 rounded text-[11px] flex items-center gap-1">
              {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
            </button>
          </div>
          <p className="text-[11px] text-slate-500">
            Values above are dummy examples. Field <b>names</b> and structure are exactly what arrives — map your system against these.
          </p>
        </div>
      )}
    </div>
  );
}

function TestResultPanel({ result, onClose }: { result: any; onClose: () => void }) {
  return (
    <div className={`border rounded-lg p-4 ${result.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          {result.ok ? <CheckCircle2 size={16} className="text-emerald-700 mt-0.5" /> : <AlertCircle size={16} className="text-rose-600 mt-0.5" />}
          <div>
            <div className={`text-sm font-medium ${result.ok ? 'text-emerald-900' : 'text-rose-900'}`}>{result.message}</div>
            {result.responseBody && (
              <>
                <div className="text-xs text-slate-500 mt-2 mb-1">Their response:</div>
                <pre className="bg-slate-900 text-emerald-300 text-[11px] p-2 rounded overflow-x-auto max-h-40"><code>{result.responseBody}</code></pre>
              </>
            )}
          </div>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
      </div>
    </div>
  );
}

function WebhookForm({ base, existing, onClose, onSaved }: { base: string; existing: any; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<any>({
    name: existing?.name ?? '',
    url: existing?.url ?? '',
    method: existing?.method ?? 'POST',
    headers: existing?.headers ? JSON.stringify(existing.headers, null, 2) : '',
  });
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const body: any = { name: form.name, url: form.url, method: form.method };
      if (form.headers?.trim()) body.headers = form.headers;
      else if (existing) body.headers = null;
      return existing
        ? (await api.patch(`${base}/${existing.id}`, body)).data
        : (await api.post(base, body)).data;
    },
    onSuccess: () => onSaved(),
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Could not save the webhook'),
  });

  return (
    <Modal onClose={onClose} title={existing ? 'Edit webhook' : 'Add webhook'}>
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Webhook name</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Dynamics AX — updateTransporter"
            className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm" />
          <p className="text-[11px] text-slate-400 mt-1">Only a label so you can tell several endpoints apart.</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">URL</label>
          <textarea value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} rows={3}
            placeholder="https://your-system.example.com/api/services/.../updateTransporter"
            className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs font-mono resize-y" />
          <p className="text-[11px] text-slate-400 mt-1">Must be a publicly reachable https:// (or http://) address.</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Method</label>
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit">
            {(['POST', 'GET'] as const).map(m => (
              <button key={m} type="button" onClick={() => setForm({ ...form, method: m })}
                className={`px-4 py-1.5 text-sm rounded-md ${form.method === m ? 'bg-white shadow-sm text-emerald-700 font-medium' : 'text-slate-600'}`}>{m}</button>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-1">
            {form.method === 'POST'
              ? 'Payload is sent as a JSON request body — recommended.'
              : 'Payload is sent as query-string parameters.'}
          </p>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Custom headers <span className="text-slate-400 font-normal">(optional)</span></label>
          <textarea value={form.headers} onChange={(e) => setForm({ ...form, headers: e.target.value })} rows={3}
            placeholder={'{\n  "Authorization": "Bearer …"\n}'}
            className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs font-mono resize-y" />
          <p className="text-[11px] text-slate-400 mt-1">JSON object. This is how you authenticate our calls if your endpoint needs it — e.g. an API key or bearer token your system already expects.</p>
        </div>
        {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded p-2">{error}</div>}
        <div className="flex items-center gap-2 pt-1">
          <button onClick={() => { setError(null); save.mutate(); }} disabled={save.isPending || !form.name || !form.url}
            className="px-4 py-2 bg-emerald-700 text-white rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-50">
            {save.isPending && <Loader2 size={13} className="animate-spin" />} {existing ? 'Save changes' : 'Create webhook'}
          </button>
          <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-lg text-sm">Cancel</button>
        </div>
      </div>
    </Modal>
  );
}

function DeliveryLog({ base, hook, onClose }: { base: string; hook: any; onClose: () => void }) {
  const { data = [], isLoading } = useQuery({
    queryKey: ['webhook-deliveries', base, hook.id],
    queryFn: async () => (await api.get(`${base}/${hook.id}/deliveries`, { params: { limit: 50 } })).data,
  });
  return (
    <Modal onClose={onClose} title={`Delivery log — ${hook.name}`} wide>
      {isLoading ? (
        <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : data.length === 0 ? (
        <div className="text-sm text-slate-500">Nothing has been sent to this endpoint yet. Press <b>Test</b> to try it, or wait for the next shipment scan.</div>
      ) : (
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 sticky top-0">
              <tr>
                <th className="text-left px-2 py-1.5">When</th>
                <th className="text-left px-2 py-1.5">Event</th>
                <th className="px-2 py-1.5">HTTP</th>
                <th className="px-2 py-1.5">Time</th>
                <th className="text-left px-2 py-1.5">Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((d: any) => (
                <tr key={d.id}>
                  <td className="px-2 py-1.5 whitespace-nowrap text-slate-600">{new Date(d.createdAt).toLocaleString('en-IN')}</td>
                  <td className="px-2 py-1.5">
                    {d.event}{d.isTest && <span className="ml-1 text-[10px] bg-slate-100 text-slate-500 px-1 rounded">test</span>}
                  </td>
                  <td className={`px-2 py-1.5 text-center font-mono ${d.ok ? 'text-emerald-700' : 'text-rose-600'}`}>{d.statusCode || '—'}</td>
                  <td className="px-2 py-1.5 text-center text-slate-500">{d.durationMs}ms</td>
                  <td className="px-2 py-1.5 text-slate-600 max-w-[320px] truncate" title={d.errorMessage ?? d.responseBody ?? ''}>
                    {d.errorMessage ?? d.responseBody ?? 'OK'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

function Modal({ title, children, onClose, wide }: { title: string; children: React.ReactNode; onClose: () => void; wide?: boolean }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className={`bg-white rounded-xl p-5 w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
